import {
   Component,
   ViewChild,
   ElementRef,
   AfterViewInit,
   HostListener,
} from '@angular/core';
import { StockOverviewDataService } from '../stock-screener/services/stock-overview-data-service.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, Subject } from 'rxjs';
import { Router } from '@angular/router';
import {
   trigger,
   style,
   transition,
   animate,
   query,
   stagger,
} from '@angular/animations';

@Component({
    selector: 'app-earnings-calendar',
    templateUrl: './earnings-calendar.component.html',
    styleUrls: ['./earnings-calendar.component.scss'],
    animations: [
        trigger('listAnimation', [
            transition(':enter', [
                query('li', [
                    style({ opacity: 0, transform: 'translateX(20px)' }),
                    stagger(100, [
                        animate('400ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
                    ]),
                ]),
            ]),
        ]),
    ],
    standalone: false
})
export class EarningsCalendarComponent {
   @ViewChild('calendarRowListWrapper') calendarRowListWrapper!: ElementRef;
   @ViewChild('searchDropdown') searchDropdown!: ElementRef;

   @HostListener('document:click', ['$event'])
   onClick(event: MouseEvent) {
      if (
         !this.searchDropdown?.nativeElement.contains(event.target) &&
         this.showSearchDropdown
      ) {
         this.showSearchDropdown = false;
      }
   }
   tabs: string[] = ['Yesterday', 'Today', 'Next Week']; // Removed 'Next Week'
   selectedTab: string = 'Today'; // Default tab
   selectedIndex: number = 0; // Track selected date i
   dataCount: number = 0;
   stocksLoading: boolean = false;
   dataType: string = 'ecal';
   activeFiltersCount: number = 0;
   page: number = 1;
   data: any = [];
   desc: string = 'desc';
   usdMarketCap: string = 'revenueEstimate';
   currentDate = new Date();
   days = this.generateDaysForTab('Today');
   selectedDate: string = '';
   searchResults: any[] = [];
   showSearchDropdown = false;
   private searchInputSubject = new Subject<string>();
   selectedTicker: string = '';
   searchQuery: string = '';
   currentPlaceholder: 'symbol' | 'company' = 'symbol';
   placeholderState: '' | 'exiting-symbol' | 'exiting-company' = '';
   private intervalId: any;
   dateCounts: { [key: string]: number } = {};
   selectedCountry: string = 'US';
   showDropdown = false;
   // Add this to your component class
   isLoadingCounts: boolean = false;
   // Add this property to your component class
   private isDateSelection: boolean = false;

   // Generate days array based on selected tab
   private generateDaysForTab(tab: string): any[] {
      const days = [];
      const date = new Date(this.currentDate);

      switch (tab) {
         case 'Yesterday':
            date.setDate(date.getDate() - 1);
            days.push(this.createDayObject(date));
            // console.log('[Yesterday] day added:', date.toDateString());
            break;

         case 'Today':
            days.push(this.createDayObject(date));
            // console.log('[Today] day added:', date.toDateString());
            break;

         case 'Next Week':
            const currentDate = new Date(date);
            const currentDay = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
            const daysUntilNextWeek = 7 - currentDay;
            const firstDayNextWeek = new Date(currentDate);
            firstDayNextWeek.setDate(currentDate.getDate() + daysUntilNextWeek);

            // console.log(
            //    '[Next Week] Starting from:',
            //    firstDayNextWeek.toDateString()
            // );

            for (let i = 0; i < 7; i++) {
               const newDate = new Date(firstDayNextWeek);
               newDate.setDate(firstDayNextWeek.getDate() + i);
               days.push(this.createDayObject(newDate));
               // console.log('[Next Week] Adding:', newDate.toDateString());
            }

            break;
      }

      return days;
   }

   resetToDefaultSelection() {
      this.selectedIndex = 0;
      this.selectedDate = this.days[0]?.apiDate;
      this.getEarningsCalendarData();
   }

   selectCountry(country: string) {
      this.selectedCountry = country;
      this.showDropdown = false;

      // Update the URL with the new country and reset to page 1
      const queryParams = {
         country: this.selectedCountry,
         page: 1, // Reset to first page when changing country
      };

      this.router.navigate([], {
         relativeTo: this.route,
         queryParams,
         queryParamsHandling: 'merge', // Preserve other query params
         replaceUrl: true,
      });

      // No need to manually call fetchDataForAllDates() here
      // because the queryParams subscription will handle it
   }
   toggleDropdown() {
      this.showDropdown = !this.showDropdown;
   }

   getCountryName(code: string): string {
      return code === 'US' ? 'USA' : 'India';
   }

   clearSearch() {
      this.page = 1; // Reset to first page when search is cleared
      this.searchQuery = '';
      this.selectedTicker = '';
      this.searchResults = [];
      this.showSearchDropdown = false;
      // Reset the counts and reload data
      this.fetchDataForAllDates().then(() => {
         this.resetToDefaultSelection();
      });
   }

   // Update days and fetch data when tab changes
   selectTab(tab: string) {
      this.page = 1; // Reset to first page when tab changes
      this.router.navigate([], {
         queryParams: { tab, page: 1 }, // Include page=1 in navigation
         queryParamsHandling: 'merge',
      });

      this.selectedTab = tab;
      this.days = this.generateDaysForTab(tab);
      this.selectedIndex = 0;
      this.selectedDate = this.days.length ? this.days[0].apiDate : null;

      this.fetchDataForAllDates().then(() => {
         this.getEarningsCalendarData();
      });
   }

   select(index: number) {
      if (this.selectedTab === 'Today' || this.selectedTab === 'Yesterday')
         return;

      this.page = 1;
      this.selectedIndex = index;
      this.selectedDate = this.days[index].apiDate;

      // Set flag to prevent full reload in ngOnInit
      this.isDateSelection = true;

      // Navigate normally
      if (this.selectedDate !== this.route.snapshot.queryParams['date']) {
         this.router.navigate([], {
            queryParams: { date: this.selectedDate, page: 1 },
            queryParamsHandling: 'merge',
         });
      }

      // Only fetch table data, don't reload counts
      this.getEarningsCalendarData();
   }

   private updateDaysForTab(tab: string) {
      const today = new Date();
      this.days = [];

      switch (tab) {
         case 'Yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            this.days.push(this.createDayObject(yesterday));
            break;

         case 'Today':
            this.days.push(this.createDayObject(today));
            break;

         case 'Next Week':
            const nextMonday = new Date(today);
            nextMonday.setDate(
               today.getDate() + ((7 - today.getDay() + 1) % 7) || 7
            );
            for (let i = 0; i < 7; i++) {
               const day = new Date(nextMonday);
               day.setDate(nextMonday.getDate() + i);
               this.days.push(this.createDayObject(day));
            }
            break;
      }

      this.selectedDate = this.days[0]?.apiDate;
   }

   private createDayObject(date: Date) {
      const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      const months = [
         'Jan',
         'Feb',
         'Mar',
         'Apr',
         'May',
         'Jun',
         'Jul',
         'Aug',
         'Sep',
         'Oct',
         'Nov',
         'Dec',
      ];

      // Format as YYYY-MM-DD for API
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const apiDate = `${year}-${month}-${day}`;

      return {
         day: days[date.getDay()],
         date: `${date.getDate()} ${months[date.getMonth()]}`,
         apiDate: apiDate, // This will be used for the API query
         earnings: 100,
      };
   }

   // Rest of your existing code...
   tableColumns = [
      {
         header: 'EPS Change',
         key: 'epsActual',
         type: 'epsEstimate',
      },
      {
         header: 'EPS Estimate',
         key: 'epsEstimate',
         type: 'epsEstimate',
      },
      //  {
      //    header: "EPS Change",
      //    key: "epsActual",
      //    type: "amount",
      //    sortable: false
      //  },
      {
         header: 'Call time',
         key: 'hour',
         type: 'callTime',
         sortable: false,
      },
      {
         header: 'Quarter',
         key: 'quarter',
         type: 'quarter',
      },
      {
         header: 'Revenue Actual',
         key: 'revenueActual',
         type: 'revenueEstimate',
      },
      {
         header: 'Revenue Eststimate',
         key: 'revenueEstimate',
         type: 'revenueEstimate',
      },
      {
         header: 'Date',
         key: 'date',
         type: 'date',
         sortable: false,
      },
   ];

   // Next Month pagination
   pageSize = 15; // 7 days per page
   currentPage = 1;

   constructor(
      private stockOverviewDataService: StockOverviewDataService,
      private route: ActivatedRoute,
      private router: Router
   ) {
      this.updateDaysForTab(this.selectedTab);

      this.searchInputSubject
         .pipe(
            debounceTime(300) // 300ms delay
         )
         .subscribe((searchText) => {
            if (searchText.length >= 2) {
               this.fetchSearchSuggestions(searchText);
            } else {
               this.searchResults = [];
            }
         });
   }

   private async fetchDataForAllDates() {
      this.stocksLoading = true;
      try {
         // Reset counts
         this.dateCounts = {};

         // Create an array of promises for each date
         const promises = this.days.map((day) =>
            this.stockOverviewDataService.getEarningsCalendar(
               this.pageSize,
               this.page,
               undefined,
               undefined,
               day.apiDate,
               this.selectedTicker,
               this.selectedCountry // Include country in the request
            )
         );

         // Wait for all requests to complete
         const results = await Promise.all(promises);

         // Store the counts
         results.forEach((result, index) => {
            this.dateCounts[this.days[index].apiDate] = result.found;
         });
      } catch (e) {
         console.error('Error fetching data for all dates:', e);
      } finally {
         this.stocksLoading = false;
      }
   }

   onSearchInput(value: string) {
      this.searchInputSubject.next(value);
      this.showSearchDropdown = !!value.trim();
      // Trigger data fetch when input is cleared
      if (value.trim() === '') {
         this.selectedTicker = '';
         this.getEarningsCalendarData();
      }
   }

   async fetchSearchSuggestions(query: string) {
      try {
         const response = await this.stockOverviewDataService.searchTickers(
            query
         );
         this.searchResults = response.hits.map((hit: any) => hit.document);
         this.showSearchDropdown = true;
         //console.log("Tickers:", this.searchResults.map((r: any) => r.company_symbol));
      } catch (e) {
         console.error('Error fetching search results:', e);
      }
   }

   // Update onSelectSearchResult to store the search query
   onSelectSearchResult(result: any) {
      this.page = 1; // Reset to first page when search result is selected
      this.selectedTicker = result.company_symbol;
      this.searchQuery = result.company_symbol;
      this.showSearchDropdown = false;
      // Fetch counts for all dates with the selected ticker filter
      this.fetchDataForAllDates().then(() => {
         this.getEarningsCalendarData();
      });

      this.searchResults = [];
   }

   async ngOnInit(): Promise<void> {
      this.route.queryParams.subscribe(async (params: any) => {
         this.page = +params.page || 1;
         const tab = params.tab || 'Today';
         const dateFromParams = params.date;
         const newCountry = params.country || 'US';

         // Check if country has changed
         const countryChanged = this.selectedCountry !== newCountry;
         this.selectedCountry = newCountry;

         // If this is just a date selection within the same tab, don't reload counts
         if (this.isDateSelection && tab === this.selectedTab && !countryChanged) {
            this.isDateSelection = false;
            return;
         }

         this.selectedTab = tab;
         this.days = this.generateDaysForTab(tab);

         if (dateFromParams) {
            const matchingIndex = this.days.findIndex(
               (d) => d.apiDate === dateFromParams
            );
            if (matchingIndex !== -1) {
               this.selectedIndex = matchingIndex;
               this.selectedDate = dateFromParams;
            } else {
               this.selectedIndex = 0;
               this.selectedDate = this.days[0]?.apiDate;
            }
         } else {
            this.selectedIndex = 0;
            this.selectedDate = this.days[0]?.apiDate;
         }

         // Always fetch data for all dates when tab or country changes
         await this.fetchDataForAllDates();
         this.getEarningsCalendarData();
      });

      this.startPlaceholderAnimation();
   }

   ngOnDestroy() {
      if (this.intervalId) {
         clearInterval(this.intervalId);
      }
   }
   startPlaceholderAnimation() {
      this.intervalId = setInterval(() => {
         // Start exit animation
         this.placeholderState =
            this.currentPlaceholder === 'symbol'
               ? 'exiting-symbol'
               : 'exiting-company';

         // After exit animation completes, switch placeholder
         setTimeout(() => {
            this.currentPlaceholder =
               this.currentPlaceholder === 'symbol' ? 'company' : 'symbol';
            this.placeholderState = '';
         }, 500); // Matches the CSS transition time
      }, 3000); // Change every 3 seconds
   }

   get visibleMonthDays() {
      const start = this.currentPage * this.pageSize;
      return this.days.slice(start, start + this.pageSize);
   }

   get isPrevDisabled(): boolean {
      return this.currentPage === 0;
   }

   get isNextDisabled(): boolean {
      return (this.currentPage + 1) * this.pageSize >= this.days.length;
   }

   nextPage() {
      if (!this.isNextDisabled) {
         this.currentPage++;
         this.scrollToPage();
      }
   }

   prevPage() {
      if (!this.isPrevDisabled) {
         this.currentPage--;
         this.scrollToPage();
      }
   }

   scrollToPage() {
      const container = this.calendarRowListWrapper.nativeElement;
      const scrollAmount = container.offsetWidth;
      container.scrollTo({
         left: this.currentPage * scrollAmount,
         behavior: 'smooth',
      });
   }

   async getEarningsCalendarData() {
      this.stocksLoading = true;
      try {
         const sortBy =
            this.route.snapshot.queryParamMap.get('sortBy') ?? undefined;
         const sortOrder =
            this.route.snapshot.queryParamMap.get('sortOrder') ?? undefined;
         const exchangeCode =
            this.route.snapshot.queryParamMap.get('country') ?? 'US'; // default to US

         const data = await this.stockOverviewDataService.getEarningsCalendar(
            this.pageSize,
            this.page,
            sortBy,
            sortOrder,
            this.selectedDate,
            this.selectedTicker,
            exchangeCode // <-- pass dynamic exchange code
         );

         this.data = data.hits.map((hit: any) => hit.document);
         this.dataCount = data.found;
      } catch (e) {
         console.error("There's an error at", e);
      } finally {
         this.stocksLoading = false;
      }
   }
}
